import React, { ReactElement } from 'react';

import { useDialogState } from '../../../../../../../../../common/hooks/useDialogState';
import {
  IndicationStatus,
  InstitutionalIndicationOrderType,
} from '../../../../../../../../../graphql';
import { useIndicationActivitySalesAndTradingContext } from '../../../../../context/IndicationActivitySalesAndTradingContext';
import { type Props as ReadButtonProps } from '../../../common/indication-section-read-view/ReadButtons';
import { PassIndicationDialog } from '../../../common/PassIndicationDialog';
import { usePassOnCoveredIndication } from '../../common/pass-covered-indication-dialog/hooks/usePassOnCoveredIndication';
import CancelCoveredIndicationConfirmDialog from '../cancel-covered-indication-confirm-dialog/CancelCoveredIndicationConfirmDialog';
import { useUpdateCoveredIndicationStatus } from './useUpdateCoveredIndicationStatus';

export type Params = Readonly<{
  isIndicationEditable: boolean;
  onStartEditing: () => void;
}>;

const useCreateSalesAndTradingReadButtonProps = ({
  isIndicationEditable,
  onStartEditing,
}: Params): ReadButtonProps & { readButtonDialogs: ReactElement[] } => {
  const {
    offeringId,
    access: { canUpdateCoveredIndication },
    complianceWarning: { hasComplianceWarning, canByPassComplianceWarning },
    coveredIndication,
  } = useIndicationActivitySalesAndTradingContext();

  const indication = coveredIndication!;

  const {
    isOpen: isPassDialogOpen,
    onOpen: openPassDialog,
    onClose: closePassDialog,
  } = useDialogState();
  const {
    isOpen: isCancelDialogOpen,
    onOpen: openCancelDialog,
    onClose: closeCancelDialog,
  } = useDialogState();

  const [updateStatus, isUpdatingStatus] = useUpdateCoveredIndicationStatus(
    offeringId,
    indication.id
  );
  const [onPass, isPassing] = usePassOnCoveredIndication({
    offeringId,
    indicationId: indication.id,
    indicationStatus: indication.status,
    onClose: closePassDialog,
  });

  return {
    baseButtonAccess: isIndicationEditable && canUpdateCoveredIndication,
    isVisible: {
      edit:
        indication.status !== IndicationStatus.Cancelled &&
        (canByPassComplianceWarning || !hasComplianceWarning),
      pass: indication.type !== InstitutionalIndicationOrderType.Pass,
      reactivate:
        indication.status === IndicationStatus.Cancelled &&
        (canByPassComplianceWarning || !hasComplianceWarning),
      cancel:
        indication.status !== IndicationStatus.Cancelled &&
        (canByPassComplianceWarning || !hasComplianceWarning),
    },
    editButtonProps: {
      disabled: isUpdatingStatus,
      onClick: onStartEditing,
    },
    menuItemProps: {
      pass: {
        disabled: isUpdatingStatus,
        onClick: openPassDialog,
      },
      reactivate: {
        disabled: isUpdatingStatus,
        onClick: () => updateStatus(IndicationStatus.Active),
      },
      cancel: {
        disabled: isUpdatingStatus,
        onClick: openCancelDialog,
      },
    },
    readButtonDialogs: [
      <PassIndicationDialog
        isOpen={isPassDialogOpen}
        isLoading={isPassing}
        onClose={closePassDialog}
        onPass={onPass}
      />,
      <CancelCoveredIndicationConfirmDialog
        indicationId={indication.id}
        offeringId={offeringId}
        isOpen={isCancelDialogOpen}
        onClose={closeCancelDialog}
      />,
    ],
  };
};

export default useCreateSalesAndTradingReadButtonProps;
